/**
 * Organizations API service using BaseApi pattern
 * Reduced from 193 lines to ~140 lines (27% reduction)
 * All features preserved: logo handling, slug/name checks, invites
 */

import { BaseApi } from './base-api';
import { createSingletonApi } from './singleton-factory';
import type {
  Organization,
  CreateOrganizationDTO,
  OrganizationInvite,
  OrganizationInviteResponse,
  OnboardingState,
  OnboardingSummary,
  AccountType
} from '@scrambl/shared';

// Response types
interface OrganizationsResponse {
  data: Organization[];
  meta: {
    total: number;
    skip: number;
    take: number;
  };
}

interface OrganizationWithLogo extends Organization {
  logoUrl: string | null;
  userMembership?: {
    id: string;
    role: string;
    status: string;
  } | null;
  clientPortalAccesses?: Array<{
    id: string;
    entityId: string;
    status: string;
  }>;
}

interface OrganizationsWithLogosResponse {
  data: OrganizationWithLogo[];
  meta: {
    total: number;
    skip: number;
    take: number;
  };
}

class OrganizationApiService extends BaseApi<Organization, CreateOrganizationDTO, Partial<Organization>> {
  constructor() {
    super({
      baseEndpoint: '/organizations',
      responseNormalization: true
    });
  }

  // Get all organizations
  async getAll({ forceRefresh = false } = {}): Promise<OrganizationsResponse> {
    return this.makeRequest<OrganizationsResponse>(this.config.baseEndpoint);
  }

  // Get all organizations with logos
  async getAllWithLogos({ forceRefresh = false } = {}): Promise<OrganizationsWithLogosResponse> {
    return this.makeRequest<OrganizationsWithLogosResponse>(`${this.config.baseEndpoint}/with-logos`);
  }

  // Get by ID
  async getById(id: string): Promise<Organization> {
    return this.findById(id);
  }

  // Get by slug
  async getBySlug(slug: string): Promise<Organization> {
    return this.makeRequest<Organization>(`${this.config.baseEndpoint}/by-slug/${slug}`);
  }

  // Create organization
  async createOrganization(data: CreateOrganizationDTO): Promise<Organization> {
    return this.create(data);
  }

  // Update organization
  async updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
    return this.update(id, data);
  }

  // Convert an account between PERSONAL and ORGANIZATION. Web only exposes the
  // personal → organization direction (org owner/admin); org → personal is
  // platform-admin only. Data is preserved either way. Returns the updated org.
  async convertAccountType(
    organizationId: string,
    target: AccountType,
  ): Promise<Organization> {
    return this.makeRequest<Organization>(
      `${this.config.baseEndpoint}/${organizationId}/convert-account-type`,
      { method: 'POST', body: { target } },
    );
  }

  // Replace the org's onboarding checklist state. Returns the persisted state.
  async updateOnboardingState(
    id: string,
    onboardingState: OnboardingState,
  ): Promise<OnboardingState> {
    const res = await this.makeRequest<{ onboardingState: OnboardingState }>(
      `${this.config.baseEndpoint}/${id}/onboarding-state`,
      { method: 'PATCH', body: { onboardingState } },
    );
    return res.onboardingState;
  }

  // Server-aggregated onboarding completion signals (counts + import rollups).
  async getOnboardingSummary(id: string): Promise<OnboardingSummary> {
    return this.makeRequest<OnboardingSummary>(
      `${this.config.baseEndpoint}/${id}/onboarding-summary`,
      { method: 'GET' },
    );
  }

  // Check name availability
  async checkNameAvailability(name: string): Promise<{ available: boolean; message?: string }> {
    return this.makeRequest<{ available: boolean; message?: string }>(
      `${this.config.baseEndpoint}/name-availability?name=${encodeURIComponent(name)}`
    );
  }

  // Check slug availability
  async checkSlugAvailability(slug: string, excludeId?: string): Promise<{ available: boolean; message?: string }> {
    const query = new URLSearchParams({ slug });
    if (excludeId) {
      query.set('excludeId', excludeId);
    }

    return this.makeRequest<{ available: boolean; message?: string }>(
      `${this.config.baseEndpoint}/slug-availability?${query.toString()}`
    );
  }

  // Invite users
  async inviteUsers(
    organizationId: string,
    data: { invites: OrganizationInvite[] }
  ): Promise<OrganizationInviteResponse> {
    return this.makeRequest<OrganizationInviteResponse>(`${this.config.baseEndpoint}/${organizationId}/invite`, {
      method: 'POST',
      body: data,
    });
  }

  // Get logo URL
  async getLogo(organizationId: string): Promise<{ url: string | null }> {
    return this.makeRequest<{ url: string | null }>(`${this.config.baseEndpoint}/${organizationId}/logo`);
  }

  // Upload logo
  async uploadLogo(organizationId: string, file: File): Promise<{ key: string; url: string }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.makeRequest<{ key: string; url: string }>(`/files/upload/organization/${organizationId}?asset=true`, {
      method: 'POST',
      body: formData,
    });
  }

  // Update logo
  async updateLogo(organizationId: string, imageKey: string): Promise<Organization> {
    return this.makeRequest<Organization>(`${this.config.baseEndpoint}/${organizationId}/logo`, {
      method: 'PUT',
      body: { imageKey },
    });
  }

  // Clear cache (no-op - TanStack Query handles caching)
  clearCache(): void {
    // No-op - TanStack Query manages cache
  }

  clearRequestCache(endpoint: string): void {
    // No-op - TanStack Query manages cache
  }
}

// Export singleton instance
const organizationApiInstance = createSingletonApi(OrganizationApiService);

// Export object-style API for backward compatibility
export const organizationsApi = {
  getAll: (options?: { forceRefresh?: boolean }) => organizationApiInstance.getAll(options),
  getAllWithLogos: (options?: { forceRefresh?: boolean }) => organizationApiInstance.getAllWithLogos(options),
  getById: (id: string) => organizationApiInstance.getById(id),
  getBySlug: (slug: string) => organizationApiInstance.getBySlug(slug),
  create: (data: CreateOrganizationDTO) => organizationApiInstance.createOrganization(data),
  update: (id: string, data: Partial<Organization>) => organizationApiInstance.updateOrganization(id, data),
  convertAccountType: (organizationId: string, target: AccountType) => organizationApiInstance.convertAccountType(organizationId, target),
  updateOnboardingState: (id: string, onboardingState: OnboardingState) => organizationApiInstance.updateOnboardingState(id, onboardingState),
  getOnboardingSummary: (id: string) => organizationApiInstance.getOnboardingSummary(id),
  checkNameAvailability: (name: string) => organizationApiInstance.checkNameAvailability(name),
  checkSlugAvailability: (slug: string, excludeId?: string) => organizationApiInstance.checkSlugAvailability(slug, excludeId),
  inviteUsers: (organizationId: string, data: { invites: OrganizationInvite[] }) => organizationApiInstance.inviteUsers(organizationId, data),
  getLogo: (organizationId: string) => organizationApiInstance.getLogo(organizationId),
  uploadLogo: (organizationId: string, file: File) => organizationApiInstance.uploadLogo(organizationId, file),
  updateLogo: (organizationId: string, imageKey: string) => organizationApiInstance.updateLogo(organizationId, imageKey),
  clearCache: () => organizationApiInstance.clearCache(),
  clearRequestCache: (endpoint: string) => organizationApiInstance.clearRequestCache(endpoint),
};

// Export class instance
export { organizationApiInstance as organizationApi };
